// DayPopover — the overflow dialog a calendar cell opens when more
// activities fall on a single day than the cell's 2-chip cap can show.
// Triggered by "+ N more"; lists EVERY activity for that day (uncapped),
// each row a keyboard-actionable <button>; selecting a row hands the
// activity id back to the consumer to open the existing ActivityDrawer.
//
// Standalone modal (NOT composed on the existing Modal): the RED
// contract requires a stable `data-testid="day-popover-backdrop"`
// handle for backdrop-dismiss testing, and the per-item button list
// is cleanest as a dedicated surface. Uses the project's
// useFocusTrap with the DEFAULT lockScroll (true) — this IS a modal,
// unlike the ActivityDrawer which deliberately opts out of body
// scroll-lock as a contextual panel. role="dialog" + aria-modal +
// per-day aria-label; three dismissal paths (Esc / backdrop /
// "Close" button); focus-trap genuinely cycles forward + backward
// inside the dialog.

import { useId, useRef } from "react";
import { X } from "lucide-react";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import type { Activity } from "../../../types/schedule";

interface DayPopoverProps {
  isOpen: boolean;
  // ISO date (YYYY-MM-DD) — used both as the dialog's accessible name
  // signal and as the rendered header text.
  date: string;
  activities: Activity[];
  onClose: () => void;
  onSelectActivity: (activityId: string) => void;
}

// Format the ISO date as a long-form heading (e.g. "Wed 20 May 2026").
// The aria-label uses the same wording so screen readers announce
// which day is shown — the RED contract accepts either the ISO date
// or a formatted form that contains the day number / month name.
function formatDayHeading(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function DayPopover({
  isOpen,
  date,
  activities,
  onClose,
  onSelectActivity,
}: DayPopoverProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const headingId = useId();

  // Focus-trap default lockScroll=true is correct here — DayPopover
  // IS a modal overlay over the page; the page should not scroll
  // behind it while open.
  useFocusTrap(dialogRef, { onEscape: onClose, enabled: isOpen });

  if (!isOpen) return null;

  const heading = formatDayHeading(date);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        data-testid="day-popover-backdrop"
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/50 dark:bg-slate-950/70"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Activities on ${heading}`}
        className="relative z-10 flex max-h-[480px] w-[320px] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
          <h2
            id={headingId}
            className="text-body-sm font-semibold text-slate-900 dark:text-slate-100"
          >
            {heading}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <ul className="flex flex-col gap-1 overflow-y-auto p-2">
          {activities.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => onSelectActivity(a.id)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:hover:bg-slate-700/60"
              >
                <span className="shrink-0 font-mono text-[10px] text-slate-400 dark:text-slate-500">
                  {a.contract.ref}
                </span>
                <span className="truncate text-body-sm text-slate-900 dark:text-slate-100">
                  {a.name}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
